import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { Box, Container, IconButton, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface Testimonial {
  quote: string;
  name: string;
  title: string;
  organization: string;
  organizationLogo?: string;
}

const testimonials: Testimonial[] = [
  {
    quote:
      "Using Grant Guardian has helped me feel more confident in my financial review skills, and it's teaching me more about the financial due diligence process along the way.",
    name: 'Jessica Van Grouw',
    title: 'Program Analyst',
    organization: 'GitLab Foundation',
  },
  {
    quote:
      'Compared to our previous manual financial due diligence process, Grant Guardian has made everything simpler, saving us significant time and money.',
    name: 'Sabra Williams',
    title: 'Manager, IT Systems',
    organization: 'United Way of Greater Philadelphia and Southern New Jersey',
  },
  {
    quote:
      "At Fund AI last year, the team shared Grant Guardian and a few other tools and resources. Having that sort of information publicly available for funders to make decisions has not just helped me with my thinking, but also it's actually raised awareness across other funders.",
    name: 'Nicole Ifill',
    title: 'Deputy Director, Data',
    organization: 'Gates Foundation',
  },
];

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentTestimonial = testimonials[currentIndex];

  const handleLogoClick = (index: number) => {
    setCurrentIndex(index);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1,
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1,
    );
  };
  return (
    <Box
      id="testimonials"
      sx={{
        py: 10,
        backgroundColor: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle, rgba(25, 118, 210, 0.08) 2px, transparent 2px)`,
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Navigation Arrows */}
        <IconButton
          onClick={handlePrevious}
          sx={{
            position: 'absolute',
            left: { xs: -20, md: -60 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: 'white',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'white',
            },
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>

        <IconButton
          onClick={handleNext}
          sx={{
            position: 'absolute',
            right: { xs: -20, md: -60 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: 'white',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'white',
            },
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Section Title */}
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
            }}
          >
            Trusted by Leading Philanthropic Institutions
          </Typography>

          {/* Decorative underline */}
          <Box
            sx={{
              width: 60,
              height: 3,
              backgroundColor: 'primary.main',
              mx: 'auto',
              mb: 6,
            }}
          />

          {/* Quote Icon */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <FormatQuoteIcon
              sx={{
                fontSize: 48,
                color: 'primary.main',
                opacity: 0.7,
              }}
            />
          </Box>

          {/* Testimonial Quote */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                maxWidth: 800,
                mx: 'auto',
                mb: 4,
                fontWeight: 400,
                lineHeight: 1.6,
                color: 'text.primary',
              }}
            >
              {currentTestimonial.quote}
            </Typography>

            {/* Author Info */}
            <Box
              sx={{
                textAlign: 'center',
                mb: 6,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {currentTestimonial.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {currentTestimonial.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {currentTestimonial.organization}
              </Typography>
            </Box>
          </motion.div>

          {/* Organization Logo for Current Testimonial */}
          {currentTestimonial.organizationLogo && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <motion.div
                key={`logo-${currentIndex}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Box
                  component="img"
                  src={currentTestimonial.organizationLogo}
                  alt={currentTestimonial.organization}
                  sx={{
                    height: 80,
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </motion.div>
            </Box>
          )}

          {/* Navigation Dots */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            {testimonials.map((_, index) => (
              <Box
                key={index}
                onClick={() => handleLogoClick(index)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor:
                    index === currentIndex ? 'primary.main' : 'grey.300',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor:
                      index === currentIndex ? 'primary.main' : 'grey.400',
                    transform: 'scale(1.2)',
                  },
                }}
              />
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}
